import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {useQueryClient} from '@tanstack/react-query'
import useOnboardingStore from '@stores/useOnboardingStore.js'
import {rehydrateAuthData} from '@utils/auth.js'
import {clearAuthSession} from '@utils/authSession.js'
import {updateRuntimeDebugState} from './debugState.js'
import {flushRuntimeTelemetry, logRuntimeEvent} from './telemetry.js'
import {buildTelemetryRoute} from './routeTelemetry.js'

const RESUME_REVALIDATE_AFTER_MS = 60_000
const RESUME_DEBOUNCE_MS = 1_500

function describeErrorMessage(error) {
  if (error instanceof Error && error.message) {
    return error.message
  }

  if (typeof error === 'string' && error.trim()) {
    return error.trim()
  }

  return 'Unknown runtime error'
}

export default function AppRuntimeController() {
  const location = useLocation()
  const queryClient = useQueryClient()
  const refreshOnboarding = useOnboardingStore(state => state.refresh)
  const lastBackgroundAtRef = useRef(null)
  const lastResumeAtRef = useRef(0)

  useEffect(() => {
    logRuntimeEvent('route_changed', {
      route: buildTelemetryRoute(location),
    }, {flush: false})
  }, [location])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const resetSignedOutQueryState = () => {
      try {
        if (typeof queryClient.cancelQueries === 'function') {
          const result = queryClient.cancelQueries()
          if (result && typeof result.catch === 'function') {
            void result.catch(error => {
              logRuntimeEvent('runtime_query_reset_failed', {
                stage: 'cancel',
                message: describeErrorMessage(error),
              }, {flush: false})
            })
          }
        }
      } catch (error) {
        logRuntimeEvent('runtime_query_reset_failed', {
          stage: 'cancel',
          message: describeErrorMessage(error),
        }, {flush: false})
      }

      try {
        if (typeof queryClient.clear === 'function') {
          queryClient.clear()
        }
      } catch (error) {
        logRuntimeEvent('runtime_query_reset_failed', {
          stage: 'clear',
          message: describeErrorMessage(error),
        }, {flush: false})
      }
    }

    const syncAuthState = () => {
      logRuntimeEvent('auth_rehydration_started', {
        source: 'runtime_resume',
      }, {flush: false})

      updateRuntimeDebugState({
        authRehydrationInFlight: true,
      })

      const auth = rehydrateAuthData()
      const isAuthenticated = Boolean(auth?.token)

      updateRuntimeDebugState({
        authHydrated: true,
        authRehydrationInFlight: false,
        authRehydrationFinishedAt: new Date().toISOString(),
      })

      logRuntimeEvent('auth_rehydration_finished', {
        source: 'runtime_resume',
        isAuthenticated,
      }, {flush: false})

      if (auth?.token) {
        return auth
      }

      clearAuthSession()
      resetSignedOutQueryState()
      return null
    }

    const handleResume = async trigger => {
      const now = Date.now()
      if (now - lastResumeAtRef.current < RESUME_DEBOUNCE_MS) {
        return
      }

      lastResumeAtRef.current = now
      const backgroundDurationMs = lastBackgroundAtRef.current
        ? Math.max(0, now - lastBackgroundAtRef.current)
        : 0

      try {
        const auth = syncAuthState()
        logRuntimeEvent('runtime_resume', {
          trigger,
          route: buildTelemetryRoute(location),
          backgroundDurationMs,
          isAuthenticated: Boolean(auth?.token),
        }, {flush: false})

        if (!auth?.token) {
          return
        }

        if (trigger === 'online' || backgroundDurationMs >= RESUME_REVALIDATE_AFTER_MS) {
          await Promise.resolve(refreshOnboarding())

          if (typeof queryClient.invalidateQueries === 'function') {
            await Promise.resolve(queryClient.invalidateQueries())
          }
        }
      } catch (error) {
        logRuntimeEvent('runtime_resume_failed', {
          trigger,
          route: buildTelemetryRoute(location),
          message: describeErrorMessage(error),
        }, {flush: false})
      } finally {
        void flushRuntimeTelemetry()
      }
    }

    const handleVisibilityChange = () => {
      if (typeof document === 'undefined') {
        return
      }

      if (document.visibilityState === 'hidden') {
        lastBackgroundAtRef.current = Date.now()
        logRuntimeEvent('app_backgrounded', {
          route: buildTelemetryRoute(location),
        }, {flush: false})
        return
      }

      void handleResume('visibilitychange')
    }

    const handleFocus = () => {
      logRuntimeEvent('runtime_focus', {
        route: buildTelemetryRoute(location),
      }, {flush: false})
      void handleResume('focus')
    }

    const handleBlur = () => {
      lastBackgroundAtRef.current = Date.now()
    }

    const handleOnline = () => {
      logRuntimeEvent('online_detected', {route: buildTelemetryRoute(location)}, {flush: false})
      void handleResume('online')
    }

    const handleOffline = () => {
      logRuntimeEvent('offline_detected', {route: buildTelemetryRoute(location)}, {flush: false})
    }

    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [location, queryClient, refreshOnboarding])

  return null
}
